import { useTerminalsState } from "@/States/Terminals";
import { trpc } from "@/Utils/trpc";
import { timezoneStore } from "@/api/hooks/useConfig";
import { CardView } from "@/components/v2/CardView";
import { PERIOD_OPTIONS } from "@/components/v2/TrendView/constants";
import { getPeriodLabelValueFromStartTime } from "@/components/v2/TrendView/utils/formatting";
import { getPeriodFilterValue } from "@/components/v2/TrendView/utils/timeframe";
import { useTrans } from "@/translations";

import { useCallback, useContext, useMemo, useState } from "react";

import { FilterTimeFrameValue } from "server/config-api/index.js";
import { Granularity } from "server/dwhApi/common.js";

import { ContaminationPageContext } from "../../context";
import { getContaminationRate, getDataWithCurrentMonth } from "../../utils";
import { ContaminationTrendGraph, type ContaminationTrendPoint } from "./graph";

export const ContaminationTrend = () => {
  const { t, language } = useTrans();
  const { timezone } = timezoneStore();
  const { currentTerminal } = useTerminalsState();
  const { selectedTenantCategoryIds } = useContext(ContaminationPageContext);
  const [period, setPeriod] = useState<FilterTimeFrameValue>(
    FilterTimeFrameValue.Last12Months,
  );

  const periodFilter = useMemo(
    () => ({
      options: PERIOD_OPTIONS,
      value: period,
      onChange: (key: string) => setPeriod(key as FilterTimeFrameValue),
    }),
    [period],
  );

  const contaminationTrendFilters = useMemo(
    () => [periodFilter],
    [periodFilter],
  );

  const periodFilterValue = useMemo(
    () => getPeriodFilterValue({ period, timezone }),
    [period, timezone],
  );

  const { data: periodData, isLoading: isPeriodLoading } =
    trpc.dwh.terminals.getPeriods.useQuery(
      {
        ...periodFilterValue,
        timeZone: timezone,
        terminalId: currentTerminal?.id || "",
        granularity: Granularity.MONTH,
        tenantCategories: selectedTenantCategoryIds || [],
      },
      {
        queryKeyHashFn: () =>
          [
            "terminalPeriods",
            periodFilterValue.startTimeISO,
            periodFilterValue.endTimeISO,
            timezone,
            currentTerminal?.id,
            selectedTenantCategoryIds,
          ].toString(),
        enabled: !!currentTerminal,
      },
    );

  const dataWithCurrentMonth = useMemo(
    () => getDataWithCurrentMonth({ periodData, period }),
    [periodData, period],
  );

  const getPeriodLabelValue = useCallback(
    (startTime: string) =>
      getPeriodLabelValueFromStartTime({
        period,
        granularity: Granularity.MONTH,
        startTime,
        language,
        timezone,
      }),
    [language, period, timezone],
  );

  const graphData: ContaminationTrendPoint[] = useMemo(() => {
    if (!periodData?.periods) {
      return [];
    }

    return dataWithCurrentMonth.map((d) => ({
      month: getPeriodLabelValue(d.startTime),
      rate: getContaminationRate(d.terminal),
    }));
  }, [periodData, dataWithCurrentMonth, getPeriodLabelValue]);

  return (
    <CardView
      title="contaminationPage:contaminationTrend"
      subtitle={t("contaminationPage:contaminationTrendSubtitle", {
        buildingName: currentTerminal?.name,
        interpolation: { escapeValue: false },
      })}
      filters={contaminationTrendFilters}
    >
      <ContaminationTrendGraph
        graphData={graphData}
        isLoading={isPeriodLoading}
      />
    </CardView>
  );
};
